import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DirectoryCardProps {
    id: string
    href: string
    icon: LucideIcon
    title: ReactNode
    description?: ReactNode
    footerLabel: ReactNode
    isEmpty?: boolean
    accent?: 'default' | 'muted'
    children: ReactNode
}

export const DirectoryCard: FC<DirectoryCardProps> = ({
    id,
    href,
    icon: Icon,
    title,
    description,
    footerLabel,
    isEmpty,
    accent = 'default',
    children
}): ReactNode => {
    return (
        <article
            id={id}
            className='flex scroll-mt-24 flex-col rounded-2xl border border-border bg-card p-4 transition-colors hover:border-border/100 sm:p-5'
        >
            <header className='flex items-start gap-3 px-1'>
                <span
                    className={cn(
                        'inline-flex size-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset',
                        accent === 'muted' ? 'bg-secondary text-muted-foreground ring-border' : 'bg-primary/10 text-primary ring-primary/25'
                    )}
                >
                    <Icon className='size-5' aria-hidden='true' />
                </span>

                <div className='min-w-0 flex-1'>
                    <h3 className='font-heading text-lg font-semibold leading-tight text-foreground'>
                        <Link href={href} className='transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-primary'>
                            {title}
                        </Link>
                    </h3>

                    {description && (
                        <p className='mt-1 text-sm leading-snug text-muted-foreground'>
                            {description}
                        </p>
                    )}
                </div>
            </header>

            <div className='mt-4 flex-1'>
                {isEmpty
                    ? (
                        <p className='rounded-xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground'>
                            Nothing listed here yet.
                        </p>
                    )
                    : children}
            </div>

            <Link
                href={href}
                className='mt-4 inline-flex items-center gap-1.5 self-start rounded-full px-1 text-sm font-semibold text-primary transition-[gap] hover:gap-2.5 focus-visible:outline-2 focus-visible:outline-primary'
            >
                {footerLabel}
                <ArrowRight className='size-4' aria-hidden='true' />
            </Link>
        </article>
    )
}
